import { useState } from 'react'
import { Grid, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'

import { Card, Skeleton } from '@repo/ui'
import { TrendChart } from 'components/analytics/TrendChart'
import { AnalyticsBlock } from 'components/display/Analytics'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useSeoGsc } from 'hooks/api'
import type { GscRowView, GscTotals } from 'services'

import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, dateTime, useTone } from '../shared'
import { delta, fmt, fmtPct, fmtPos, sourceText } from './seoModel'

type View = 'queries' | 'pages'
const VIEWS: readonly ViewToggleOption<View>[] = [
  { value: 'queries', label: 'Queries' },
  { value: 'pages', label: 'Pages' },
]
const COMPARE: readonly ViewToggleOption<'off' | 'on'>[] = [
  { value: 'off', label: 'This period' },
  { value: 'on', label: 'Compare to previous' },
]
const TOP = 50

const signed = (d: number | null, digits = 0) =>
  d === null ? '' : `${d > 0 ? '+' : d < 0 ? '−' : '±'}${Math.abs(d).toFixed(digits)}`

const Delta = ({
  now,
  before,
  better = 'up',
  digits = 0,
}: {
  now: number | null
  before: number | null
  better?: 'up' | 'down'
  digits?: number
}) => {
  const tone = useTone()
  const d = delta(now, before)
  if (d === null) return null
  const good = better === 'up' ? d > 0 : d < 0
  return (
    <Typography
      component="span"
      variant="body3"
      sx={{
        ml: 1,
        color: d === 0 ? 'text.secondary' : tone(good ? 'positive' : 'negative').color,
      }}
    >
      {signed(d, digits)}
    </Typography>
  )
}

const RowsTable = ({
  rows,
  label,
  compare,
}: {
  rows: GscRowView[]
  label: string
  compare: boolean
}) => (
  <StatTable>
    <TableHead>
      <TableRow>
        <HeadCell>{label}</HeadCell>
        {label === 'Page' ? <HeadCell>Top query</HeadCell> : null}
        <HeadCell align="right">Clicks</HeadCell>
        <HeadCell align="right">Impressions</HeadCell>
        <HeadCell align="right">CTR</HeadCell>
        <HeadCell align="right">Position</HeadCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <EmptyRow
          colSpan={label === 'Page' ? 6 : 5}
          text="Search Console has nothing for this range."
        />
      ) : (
        rows.slice(0, TOP).map((r) => (
          <TableRow key={r.key}>
            <Cell strong>{r.key}</Cell>
            {label === 'Page' ? <Cell>{r.topQuery ?? '—'}</Cell> : null}
            <Cell align="right">
              {fmt(r.clicks)}
              {compare ? <Delta now={r.clicks} before={r.previous?.clicks ?? null} /> : null}
            </Cell>
            <Cell align="right">
              {fmt(r.impressions)}
              {compare ? (
                <Delta now={r.impressions} before={r.previous?.impressions ?? null} />
              ) : null}
            </Cell>
            <Cell align="right">{fmtPct(r.ctr)}</Cell>
            <Cell align="right">
              {fmtPos(r.position)}
              {compare ? (
                <Delta
                  now={r.position}
                  before={r.previous?.position ?? null}
                  better="down"
                  digits={1}
                />
              ) : null}
            </Cell>
          </TableRow>
        ))
      )}
    </TableBody>
  </StatTable>
)

/*
  Each tile explains ITS metric from the glossary (gsc-clicks, gsc-impressions, gsc-ctr,
  gsc-average-position), and each of those names the source — "Google Search Console,
  synced nightly, 2–3 day lag". It used to pass `note: 'Google Search Console'` on every
  tile, which AnalyticsBlock treats as a CAVEAT: four identical tooltips that said only the
  source, each behind a caveat-marked (i) unlike any other page (Spencer, 2026-10-02).
  The comparison figure is a basis line under the number (`unit`), not a caveat.
*/
const Tiles = ({ totals, prev }: { totals: GscTotals; prev: GscTotals | null }) => (
  <Grid container spacing={4}>
    {(
      [
        ['Clicks', 'gsc-clicks', fmt(totals.clicks), prev ? fmt(prev.clicks) : undefined],
        ['Impressions', 'gsc-impressions', fmt(totals.impressions), prev ? fmt(prev.impressions) : undefined],
        ['CTR', 'gsc-ctr', fmtPct(totals.ctr), prev ? fmtPct(prev.ctr) : undefined],
        ['Average position', 'gsc-average-position', fmtPos(totals.position), prev ? fmtPos(prev.position) : undefined],
      ] as const
    ).map(([title, term, stat, before]) => (
      <Grid item xs={12} sm={6} md={3} key={title}>
        <AnalyticsBlock
          title={title}
          term={term}
          stat={stat}
          unit={before !== undefined ? `Previous period: ${before}` : undefined}
        />
      </Grid>
    ))}
  </Grid>
)

/**
 * vera.inc in Google Search, from Search Console (synced nightly into `gsc_daily` — this
 * card reads Vera's table, not Google). Search Console lags 2–3 days, so the last days of
 * any range are thin. Query rows never add up to page totals: Google withholds rare
 * queries for privacy, and the gap is shown rather than hidden.
 */
export const SeoGscCard = ({ range }: { range: { since: string; until: string } }) => {
  const [view, setView] = useState<View>('queries')
  const [compare, setCompare] = useState<'off' | 'on'>('off')
  const { data, isLoading, error } = useSeoGsc(range, compare === 'on')

  return (
    <Card
      title="Search Console"
      action={
        <ViewToggle options={COMPARE} value={compare} onChange={setCompare} ariaLabel="Compare" />
      }
    >
      <Stack spacing={4} sx={{ mt: 4 }}>
        {isLoading ? (
          <Skeleton height={14} lines={8} />
        ) : error || !data ? (
          <SectionNote>
            Could not load Search Console: {(error as Error | null)?.message}
          </SectionNote>
        ) : (
          <>
            {sourceText('Search Console', data.state) ? (
              <Notice severity="warning">{sourceText('Search Console', data.state)}</Notice>
            ) : null}
            <Tiles totals={data.totals} prev={data.previousTotals} />
            <Stack spacing={1}>
              <Typography variant="body3" color="text.secondary">
                Impressions per day, {data.range.since} to {data.range.until}
              </Typography>
              <TrendChart
                data={data.daily}
                xKey="date"
                series={[{ dataKey: 'impressions', label: 'Impressions' }]}
                ariaLabel="Search Console impressions per day"
              />
            </Stack>
            <SectionNote>
              {data.anonymisedImpressionShare !== null ? (
                <>
                  {fmtPct(data.anonymisedImpressionShare)} of impressions belong to queries Google
                  does not disclose
                  <InfoTooltip term="gsc-anonymised-queries" />, so the query table covers the rest.{' '}
                </>
              ) : null}
              Last synced {data.lastSyncedAt ? dateTime(data.lastSyncedAt) : 'never'}
              <InfoTooltip term="gsc-data-lag" />.
              {data.compare ? ` Compared with ${data.compare.since} to ${data.compare.until}.` : ''}
            </SectionNote>
            <Stack direction="row" justifyContent="flex-end">
              <ViewToggle
                options={VIEWS}
                value={view}
                onChange={setView}
                ariaLabel="Search Console dimension"
              />
            </Stack>
            <RowsTable
              rows={view === 'queries' ? data.queries : data.pages}
              label={view === 'queries' ? 'Query' : 'Page'}
              compare={compare === 'on'}
            />
          </>
        )}
      </Stack>
    </Card>
  )
}
